import { Stack } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { pbjShortShiftReviewSortingKey } from "../../../services/LocalStorageService";
import { usePbjShortShiftReview } from "../hooks/usePbjAudit";
import { ShiftDurationReviewSection } from "./ShiftDurationReviewSection";

type Props = {
	startDate: string;
	endDate: string;
};

/**
 * Read-only Short Shifts audit: the low-end mirror of the Long Shift Review — one row per individual
 * shift whose gross clock-in/out duration is under a fixed threshold (sourced from the response, not
 * hardcoded). A sub-hour punch pair is almost always a bad punch, not real direct-care time.
 *
 * Single signal, so no threshold editor and no second grid: the zero-length missing-punch markers the
 * synchers write are filtered server-side, because they'd otherwise fill this tab with 0.00h rows that
 * the Missed Punches tab already owns.
 */
export const ShortShiftsTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const review = usePbjShortShiftReview(startDate, endDate);

	return (
		<Stack spacing={4} data-testid="pbj-short-shifts-tab">
			<ShiftDurationReviewSection
				title={t("pbj_model.audit.short_shifts.review.title")}
				// Threshold comes from the response (undefined while loading), so only show the number-bearing
				// subtitle once loaded — avoids an "under  hours" flash and keeps the value single-sourced on the BE.
				description={review.data ? t("pbj_model.audit.short_shifts.review.subtitle", { hours: review.data.thresholdHours }) : undefined}
				emptyTitle={t("pbj_model.audit.short_shifts.review.empty", { hours: review.data?.thresholdHours })}
				errorMessage={t("pbj_model.audit.short_shifts.review.failed")}
				rows={review.data?.rows ?? []}
				loading={review.isLoading}
				error={review.isError}
				sectionTestId="pbj-short-shift-review-section"
				tableTestId="pbj-short-shift-review-table"
				emptyTestId="pbj-short-shift-review-empty"
				sortingLocalStorageKey={pbjShortShiftReviewSortingKey}
			/>
		</Stack>
	);
};
